import { useEffect, useState } from 'react';

import { getPremiumSessionCollateral } from 'services/PremiumSessionService';
import { extractFromSlug } from 'helpers/utils';
import { LandingTab } from 'components/Landing/types';

import type CatalogStore from 'stores/Catalog';
import type { RouterStore } from 'mobx-router5';
import type UIStore from 'stores/UI';
import type LiveClassStore from 'stores/LiveClass';

type UseCourseLandingRedirectAndDataOptions = {
  courseSlug: string | undefined;
  catalogStore: CatalogStore;
  uiStore: UIStore;
  routerStore: RouterStore;
  liveClassStore: LiveClassStore;
  fetchOverviewData: (code: string, version: string) => Promise<void>;
  externalLoadingError: () => void;
};

/**
 * Handles Course Landing redirect logic and data fetching:
 * - Syncs selectedVersion from URL slug
 * - Redirects to Premium Landing when catalog has no course but premium session exists
 * - Fetches course overview data when staying on Course Landing
 * - Cleanup on unmount
 */
export default function useCourseLandingRedirectAndData({
  courseSlug,
  catalogStore,
  uiStore,
  routerStore,
  liveClassStore,
  fetchOverviewData,
  externalLoadingError,
}: UseCourseLandingRedirectAndDataOptions) {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [redirectToPremiumLanding, setRedirectToPremiumLanding] =
    useState(false);
  const [premiumRedirectCheckComplete, setPremiumRedirectCheckComplete] =
    useState(false);

  useEffect(() => {
    setPremiumRedirectCheckComplete(false);
  }, [courseSlug, selectedVersion]);

  // Sync selectedVersion from slug and live class offering
  useEffect(() => {
    if (!courseSlug) return;

    // eslint-disable-next-line no-param-reassign -- mutating store state
    liveClassStore.offeringSlug = courseSlug;
    const { version } = extractFromSlug(courseSlug);

    if (version) {
      setSelectedVersion(version);
    } else {
      externalLoadingError();
    }
  }, [courseSlug, externalLoadingError, liveClassStore]);

  // Redirect to Premium when no catalog entry but premium session exists
  useEffect(() => {
    if (
      !courseSlug ||
      !catalogStore.hasCatalogLoaded ||
      !selectedVersion ||
      redirectToPremiumLanding
    ) {
      return;
    }

    const { code, version } = extractFromSlug(courseSlug);
    const catalogEntry = catalogStore.getCatalogEntryForCourse(
      code.toLowerCase(),
      version.toLowerCase(),
    );

    if (catalogEntry) {
      setPremiumRedirectCheckComplete(true);
      return;
    }

    const sessionSlug = `${courseSlug}-s01`;
    const locale = uiStore.currentLanguage || 'en-US';

    getPremiumSessionCollateral(sessionSlug, locale, true, 'en-US')
      .then(() => {
        setRedirectToPremiumLanding(true);
        routerStore.navigate(
          'premium:sessions:overview',
          { session: courseSlug, tab: LandingTab.PremiumSessions },
          // @ts-ignore - router5 options
          { replace: true },
        );
      })
      .catch(() => {
        setPremiumRedirectCheckComplete(true);
      });
  }, [
    catalogStore,
    catalogStore.hasCatalogLoaded,
    courseSlug,
    redirectToPremiumLanding,
    routerStore,
    selectedVersion,
    uiStore.currentLanguage,
  ]);

  // Fetch course data when staying on Course Landing
  useEffect(() => {
    if (
      !courseSlug ||
      !catalogStore.hasCatalogLoaded ||
      !selectedVersion ||
      redirectToPremiumLanding ||
      !premiumRedirectCheckComplete
    ) {
      return undefined;
    }

    const { code } = extractFromSlug(courseSlug);
    fetchOverviewData(code, selectedVersion).catch(() => {});

    return () => {
      uiStore.reportActiveCourseTranslations(null);
    };
  }, [
    courseSlug,
    selectedVersion,
    catalogStore.hasCatalogLoaded,
    premiumRedirectCheckComplete,
    redirectToPremiumLanding,
    fetchOverviewData,
    uiStore,
  ]);

  return {
    selectedVersion,
    setSelectedVersion,
    redirectToPremiumLanding,
    premiumRedirectCheckComplete,
  };
}
